/**
 * Keeps existing work visible through the banner, bell, tab title and favicon.
 * Queue counts are snapshots, not arrival events: initial loading, polling,
 * focus refreshes and reconnects can all raise them without a new order.
 * Audio belongs to the actual socket event handlers, never to these counts.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMenuBadges } from "./useMenuBadges";
import {
  createFaviconDot,
  createTitleNudge,
  describeAttention,
} from "../utilities/attentionState";

export default function useAttentionNudge() {
  const badges = useMenuBadges();
  const attention = useMemo(() => describeAttention(badges), [badges]);
  const count = attention?.count || 0;

  const titleRef = useRef(null);
  const faviconRef = useRef(null);

  // The banner can be put away, but only until the next arrival, and putting it
  // away changes nothing else: the bell keeps pulsing, the title keeps flipping
  // and the count keeps counting. Nothing here can silence unacted work.
  const [hiddenAtCount, setHiddenAtCount] = useState(0);
  const dismissBanner = useCallback(() => setHiddenAtCount(count), [count]);

  useEffect(() => {
    if (count === 0 && hiddenAtCount !== 0) setHiddenAtCount(0);
  }, [count, hiddenAtCount]);

  // Tab title and favicon.
  useEffect(() => {
    if (!titleRef.current) titleRef.current = createTitleNudge();
    if (!faviconRef.current) faviconRef.current = createFaviconDot();
    if (count > 0) {
      titleRef.current.start(count);
      faviconRef.current.start();
    } else {
      titleRef.current.stop();
      faviconRef.current.stop();
    }
  }, [count]);

  // Leaving the dashboard must hand the page back its own title and icon.
  useEffect(
    () => () => {
      if (titleRef.current) titleRef.current.stop();
      if (faviconRef.current) faviconRef.current.stop();
    },
    []
  );

  return {
    /** True only while there is unacted new work. Everything is off at false. */
    active: count > 0,
    count,
    message: attention?.message || "",
    path: attention?.path || null,
    items: attention?.items || [],
    // Hidden until the count RISES past where it was put away: clearing one
    // order must not throw the banner back in the operator's face.
    bannerVisible: count > 0 && count > hiddenAtCount,
    dismissBanner,
  };
}
